import type { ReactElement, RefObject } from 'react'
import type { CanvasViewport } from '../../types/infrastructure-map'
import type { CaseEndpoint } from '../../types'
import type { EndpointRelationship } from '../../types/case-container/relationships'
import type { useInfrastructureMap } from '../../hooks/use-infrastructure-map'
import {
  EndpointDetailsModal,
  ExpandConfirmationDialog,
  LinkContextMenu,
  LinkCreationTool,
  LinkDetailsModal,
  MapToolbar,
  NodeContextMenu,
  OrphanPanel,
  type LinkContextMenuState,
  type NodeContextMenuState,
} from './canvas'
import { CanvasNodesLayer } from './CanvasNodesLayer'
import { CanvasSvgLayer } from './infrastructure-map-canvas-svg'
import type { CanvasDerivedData } from './infrastructure-map-canvas.derived'
import type { CanvasHandlers } from './infrastructure-map-canvas.handlers'
import type {
  usePanZoom,
  useNodeSelection,
  useNodeDragHandlers,
  useContextMenuActions,
} from './infrastructure-map-canvas.hooks'

const CANVAS_SIZE = { width: 4000, height: 3000 }

type InfraMapApi = ReturnType<typeof useInfrastructureMap>
type PanZoomHandlers = ReturnType<typeof usePanZoom>
type SelectionHandler = ReturnType<typeof useNodeSelection>
type DragHandlersApi = ReturnType<typeof useNodeDragHandlers>
type ContextActionsApi = ReturnType<typeof useContextMenuActions>

interface CanvasToolbarProps {
  infraMap: InfraMapApi
  panZoom: PanZoomHandlers
}

export function CanvasToolbar({ infraMap, panZoom }: CanvasToolbarProps): ReactElement {
  return (
    <MapToolbar
      currentStyle={infraMap.mapStyle}
      onStyleChange={infraMap.setMapStyle}
      isLinkCreationActive={infraMap.linkCreation.state.isActive}
      onToggleLinkCreation={() => {
        if (infraMap.linkCreation.state.isActive) {
          infraMap.linkCreation.reset()
        } else {
          infraMap.linkCreation.startLinkCreation()
        }
      }}
      onRecalculateLayout={() => {
        void infraMap.recalculateLayout()
      }}
      onZoomIn={panZoom.zoomIn}
      onZoomOut={panZoom.zoomOut}
      onResetZoom={panZoom.resetZoom}
      canUndo={false}
      canRedo={false}
      onUndo={() => {}}
      onRedo={() => {}}
    />
  )
}

export interface MapCanvasAreaProps {
  containerRef: RefObject<HTMLDivElement | null>
  canvasRef: RefObject<HTMLDivElement | null>
  isPanning: boolean
  viewport: CanvasViewport
  panZoom: PanZoomHandlers
  selection: SelectionHandler
  dragHandlers: DragHandlersApi
  infraMap: InfraMapApi
  derived: CanvasDerivedData
  selectedEndpointIds: Set<string>
  taggedEndpointIds: Set<string>
  editLinkId: string | null
  handlers: Pick<
    CanvasHandlers,
    'handleCanvasClick' | 'handleNodeContextMenu' | 'handleLinkContextMenu' | 'handleAddToMap'
  >
}

interface CanvasContentLayersProps {
  derived: CanvasDerivedData
  infraMap: InfraMapApi
  selectedEndpointIds: Set<string>
  taggedEndpointIds: Set<string>
  editLinkId: string | null
  viewportZoom: number
  selection: SelectionHandler
  dragHandlers: DragHandlersApi
  handlers: Pick<CanvasHandlers, 'handleNodeContextMenu' | 'handleLinkContextMenu'>
}

function CanvasContentLayers({
  derived,
  infraMap,
  selectedEndpointIds,
  taggedEndpointIds,
  editLinkId,
  viewportZoom,
  selection,
  dragHandlers,
  handlers,
}: CanvasContentLayersProps): ReactElement {
  const { busTopologyData, treeTopologyData, linksToRender, collapsedPositions, nodesToRender } =
    derived
  return (
    <>
      <GridBackground />
      <CanvasSvgLayer
        mapStyle={infraMap.mapStyle}
        busTopologyData={busTopologyData}
        treeTopologyData={treeTopologyData}
        linksToRender={linksToRender}
        summaryTiles={infraMap.summaryTiles}
        displayInfo={infraMap.displayInfo}
        collapsedPositions={collapsedPositions}
        editLinkId={editLinkId}
        toggleCollapse={infraMap.toggleCollapse}
        toggleCollapseRecursive={infraMap.toggleCollapseRecursive}
        onLinkContextMenu={handlers.handleLinkContextMenu}
      />
      <CanvasNodesLayer
        nodes={nodesToRender}
        getEndpointById={derived.getEndpointById}
        selectedEndpointIds={selectedEndpointIds}
        taggedEndpointIds={taggedEndpointIds}
        linkCreationState={infraMap.linkCreation.state}
        viewportZoom={viewportZoom}
        onNodeClick={selection.onNodeClick}
        onNodeContextMenu={handlers.handleNodeContextMenu}
        onNodeDrag={dragHandlers.onDrag}
        onNodeDragEnd={dragHandlers.onDragEnd}
      />
    </>
  )
}

export function MapCanvasArea({
  containerRef,
  canvasRef,
  isPanning,
  viewport,
  panZoom,
  selection,
  dragHandlers,
  infraMap,
  derived,
  selectedEndpointIds,
  taggedEndpointIds,
  editLinkId,
  handlers,
}: MapCanvasAreaProps): ReactElement {
  const { nodesToRender, orphanEndpoints } = derived
  const transform = `translate(${String(viewport.x)}px, ${String(viewport.y)}px) scale(${String(viewport.zoom)})`
  return (
    <div className="flex flex-1 overflow-hidden">
      <div
        ref={containerRef as RefObject<HTMLDivElement>}
        className={`relative flex-1 overflow-hidden bg-slate-50 dark:bg-slate-900/50 ${
          isPanning ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onMouseDown={panZoom.onPanStart}
        onMouseMove={panZoom.onPanMove}
        onMouseUp={panZoom.onPanEnd}
        onMouseLeave={panZoom.onPanEnd}
        onWheel={panZoom.onWheel}
        onClick={handlers.handleCanvasClick}
      >
        <div
          ref={canvasRef as RefObject<HTMLDivElement>}
          className="absolute"
          style={{
            width: CANVAS_SIZE.width,
            height: CANVAS_SIZE.height,
            transform,
            transformOrigin: '0 0',
          }}
        >
          <CanvasContentLayers
            derived={derived}
            infraMap={infraMap}
            selectedEndpointIds={selectedEndpointIds}
            taggedEndpointIds={taggedEndpointIds}
            editLinkId={editLinkId}
            viewportZoom={viewport.zoom}
            selection={selection}
            dragHandlers={dragHandlers}
            handlers={handlers}
          />
        </div>
        <ZoomIndicator zoom={viewport.zoom} />
        <HelpHint />
        {nodesToRender.length === 0 && orphanEndpoints.length === 0 && <EmptyState />}
      </div>
      {orphanEndpoints.length > 0 && (
        <OrphanPanel
          orphanEndpoints={orphanEndpoints}
          onDragToCanvas={() => {}}
          onAddToMap={handlers.handleAddToMap}
        />
      )}
    </div>
  )
}

export interface MapCanvasOverlaysProps {
  linkCreation: InfraMapApi['linkCreation']
  nodeContextMenu: NodeContextMenuState | null
  contextActions: ContextActionsApi
  detailsEndpoint: CaseEndpoint | null
  setDetailsEndpointId: React.Dispatch<React.SetStateAction<string | null>>
  linkContextMenu: LinkContextMenuState | null
  editingRelationship: EndpointRelationship | null | undefined
  getEndpointById: (id: string) => CaseEndpoint | undefined
  pendingExpandConfirmation: InfraMapApi['pendingExpandConfirmation']
  confirmRecursiveAction: InfraMapApi['confirmRecursiveAction']
  cancelRecursiveAction: InfraMapApi['cancelRecursiveAction']
  handleViewLinkDetails: () => void
  handleCloseLinkModal: () => void
  handleLinkComplete: CanvasHandlers['handleLinkComplete']
}

export function MapCanvasOverlays({
  linkCreation,
  nodeContextMenu,
  contextActions,
  detailsEndpoint,
  setDetailsEndpointId,
  linkContextMenu,
  editingRelationship,
  getEndpointById,
  pendingExpandConfirmation,
  confirmRecursiveAction,
  cancelRecursiveAction,
  handleViewLinkDetails,
  handleCloseLinkModal,
  handleLinkComplete,
}: MapCanvasOverlaysProps): ReactElement {
  return (
    <>
      <LinkCreationTool
        isActive={linkCreation.state.isActive}
        parentEndpointId={linkCreation.state.parentEndpointId}
        childEndpointIds={linkCreation.state.childEndpointIds}
        onToggleChild={linkCreation.toggleChild}
        onComplete={handleLinkComplete}
        onCancel={linkCreation.reset}
      />
      {nodeContextMenu && (
        <NodeContextMenu
          menu={nodeContextMenu}
          onChangeTileSize={contextActions.onChangeTileSize}
          onAddToMapSet={contextActions.onAddToMapSet}
          onRemoveFromMapSet={contextActions.onRemoveFromMapSet}
          onAddSelectedToCollectionSet={contextActions.onAddSelectedToCollectionSet}
          onRemoveFromCollectionSet={contextActions.onRemoveFromCollectionSet}
          onAddToCollectionSet={contextActions.onAddToCollectionSet}
          onViewDetails={contextActions.onViewDetails}
        />
      )}
      {detailsEndpoint && (
        <EndpointDetailsModal
          endpoint={detailsEndpoint}
          onClose={() => {
            setDetailsEndpointId(null)
          }}
        />
      )}
      {linkContextMenu && (
        <LinkContextMenu menu={linkContextMenu} onViewLinkDetails={handleViewLinkDetails} />
      )}
      {editingRelationship && (
        <LinkDetailsModal
          relationship={editingRelationship}
          sourceEndpoint={getEndpointById(editingRelationship.sourceEndpointId)}
          targetEndpoint={getEndpointById(editingRelationship.targetEndpointId)}
          onClose={handleCloseLinkModal}
        />
      )}
      <ExpandConfirmationDialog
        isOpen={pendingExpandConfirmation !== null}
        isExpanding={pendingExpandConfirmation?.isExpanding ?? false}
        affectedCount={pendingExpandConfirmation?.affectedCount ?? 0}
        onConfirm={(...args: Parameters<typeof confirmRecursiveAction>) => {
          void confirmRecursiveAction(...args)
        }}
        onCancel={cancelRecursiveAction}
      />
    </>
  )
}

function GridBackground(): ReactElement {
  return (
    <div
      className="absolute inset-0 opacity-30 dark:opacity-20"
      style={{
        backgroundImage: `
          linear-gradient(to right, rgb(148 163 184 / 0.3) 1px, transparent 1px),
          linear-gradient(to bottom, rgb(148 163 184 / 0.3) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
      }}
    />
  )
}

function ZoomIndicator({ zoom }: { zoom: number }): ReactElement {
  return (
    <div className="absolute bottom-4 left-4 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
      {String(Math.round(zoom * 100))}%
    </div>
  )
}

function HelpHint(): ReactElement {
  return (
    <div className="absolute right-4 bottom-4 text-xs text-slate-400 dark:text-slate-500">
      Drag to pan • Ctrl+scroll to zoom • Drag nodes to reposition
    </div>
  )
}

function EmptyState(): ReactElement {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="rounded-lg bg-white p-6 text-center text-slate-500 shadow-lg dark:bg-slate-800 dark:text-slate-400">
        <svg
          className="mx-auto mb-3 h-12 w-12 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
        <p className="font-medium">No endpoints to display</p>
        <p className="text-sm">Import endpoints or add them manually</p>
      </div>
    </div>
  )
}
